import React, { useState, useEffect } from 'react';

const InteractiveChart = () => {
  const [activePoint, setActivePoint] = useState(null);
  const [animationProgress, setAnimationProgress] = useState(0);

  // Sample stock data
  const stockData = [
    { year: '2015', value: 50, label: 'Starting Point' },
    { year: '2016', value: 65, label: 'Steady Growth' },
    { year: '2017', value: 80, label: 'Market Expansion' },
    { year: '2018', value: 75, label: 'Market Volatility' },
    { year: '2019', value: 95, label: 'Recovery Phase' },
    { year: '2020', value: 85, label: 'Pandemic Impact' },
    { year: '2021', value: 150, label: 'Bull Market' },
    { year: '2022', value: 120, label: 'Market Correction' },
    { year: '2023', value: 180, label: 'Growth Phase' },
    { year: '2024', value: 200, label: 'Current Value' },
    { year: '2025', value: 220, label: 'Projected Growth' }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationProgress(100);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const maxValue = Math.max(...stockData.map(d => d.value));
  const chartHeight = 200;
  const chartWidth = 400;
  const padding = 40;

  const getX = (index) => padding + (index * (chartWidth - 2 * padding)) / (stockData.length - 1);
  const getY = (value) => chartHeight - padding - ((value / maxValue) * (chartHeight - 2 * padding));

  const pathData = stockData.map((point, index) => {
    const x = getX(index);
    const y = getY(point.value);
    return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).join(' ');

  return (
    <div className="interactive-chart-container">
      <h4 className="text-center mb-3">📈 Interactive Market Growth</h4>
      <div className="chart-wrapper position-relative">
        <svg 
          width={chartWidth} 
          height={chartHeight} 
          className="interactive-chart"
          style={{ maxWidth: '100%', height: 'auto' }}
        >
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e0e0e0" strokeWidth="1"/>
            </pattern>
            <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3498db" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#3498db" stopOpacity="0.05"/>
            </linearGradient>
          </defs>
          
          <rect width="100%" height="100%" fill="url(#grid)" opacity="0.5"/>
          
          {/* Chart area fill */}
          <path
            d={`${pathData} L ${getX(stockData.length - 1)} ${chartHeight - padding} L ${getX(0)} ${chartHeight - padding} Z`}
            fill="url(#chartGradient)"
            style={{
              strokeDasharray: 1000,
              strokeDashoffset: animationProgress === 100 ? 0 : 1000,
              transition: 'stroke-dashoffset 2s ease-in-out'
            }}
          />
          
          {/* Chart line */}
          <path
            d={pathData}
            fill="none"
            stroke="#3498db"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 1000,
              strokeDashoffset: animationProgress === 100 ? 0 : 1000,
              transition: 'stroke-dashoffset 2s ease-in-out'
            }}
          />
          
          {/* Data points */}
          {stockData.map((point, index) => (
            <g key={index}>
              <circle
                cx={getX(index)}
                cy={getY(point.value)}
                r={activePoint === index ? 8 : 5}
                fill="#3498db"
                stroke="white"
                strokeWidth="2"
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: animationProgress === 100 ? 1 : 0
                }}
                onMouseEnter={() => setActivePoint(index)}
                onMouseLeave={() => setActivePoint(null)}
              />
              
              {/* Invisible larger circle for better hover detection */}
              <circle
                cx={getX(index)}
                cy={getY(point.value)}
                r={15}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setActivePoint(index)}
                onMouseLeave={() => setActivePoint(null)}
              />
              
              {/* Year labels */}
              <text
                x={getX(index)}
                y={chartHeight - 10}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
                style={{
                  opacity: animationProgress === 100 ? 1 : 0,
                  transition: 'opacity 0.5s ease'
                }}
              >
                {point.year}
              </text>
              
              {/* Value labels */}
              <text
                x={getX(index)}
                y={getY(point.value) - 15}
                textAnchor="middle"
                fontSize="11"
                fill="#3498db"
                fontWeight="bold"
                style={{
                  opacity: activePoint === index ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}
              >
                ${point.value}
              </text>
            </g>
          ))}
        </svg>
        
        {/* Tooltip */}
        {activePoint !== null && (
          <div 
            className="chart-tooltip"
            style={{
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.8)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '14px',
              pointerEvents: 'none',
              zIndex: 10
            }}
          >
            <strong>{stockData[activePoint].year}</strong>: {stockData[activePoint].label}
            <br />
            <span style={{ color: '#3498db' }}>Value: ${stockData[activePoint].value}</span>
          </div>
        )}
      </div>
      
      <div className="chart-legend mt-3">
        <div className="row text-center">
          <div className="col">
            <small className="text-muted">
              <span style={{ color: '#27ae60' }}>●</span> Growth Trend
            </small>
          </div>
          <div className="col">
            <small className="text-muted">
              <span style={{ color: '#e74c3c' }}>●</span> Market Volatility
            </small>
          </div>
          <div className="col">
            <small className="text-muted">
              <span style={{ color: '#3498db' }}>●</span> Long-term Value
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveChart;
